import { GQL } from '@aline/graphql';
import { z } from 'zod';

import { IntakeFormValuesSchema } from './intake-forms/types';

const DESCRIPTION_MAX_LENGTH = 2000;
export const WORKFLOW_AI_NAME_MAX_LENGTH = 2000;

export type WorkflowAiLifecycleState = 'building' | 'cancelled' | 'failed' | 'finished' | 'idle';

type WorkflowAiLifecycleTimestamp = Date | string | null;

export interface WorkflowAiLifecycleTimestamps {
  aiCancelledAt?: WorkflowAiLifecycleTimestamp;
  aiFailedAt?: WorkflowAiLifecycleTimestamp;
  aiFinishedAt?: WorkflowAiLifecycleTimestamp;
  aiStartedAt?: WorkflowAiLifecycleTimestamp;
}

export function getWorkflowAiLifecycleState(
  workflow: WorkflowAiLifecycleTimestamps
): WorkflowAiLifecycleState {
  if (workflow.aiCancelledAt) return 'cancelled';
  if (workflow.aiFailedAt) return 'failed';
  if (workflow.aiFinishedAt) return 'finished';
  if (workflow.aiStartedAt) return 'building';
  return 'idle';
}

const InternalPathSchema = z
  .string()
  .regex(/^\/\S+/, 'must be a non-empty internal path starting with /');

export const DocumentIdSchema = z.uuid().brand<'DocumentId'>();
export type DocumentId = z.infer<typeof DocumentIdSchema>;

export const WorkflowIdSchema = z.uuid().brand<'WorkflowId'>();
export type WorkflowId = z.infer<typeof WorkflowIdSchema>;

export const WorkflowAiDocumentAttachmentSchema = z.object({
  documentId: DocumentIdSchema,
  documentRole: z
    .nativeEnum(GQL.WorkflowFileDocumentRole)
    .describe('Whether the document is a general attachment or part of the signing package.'),
});
export type WorkflowAiDocumentAttachment = z.infer<typeof WorkflowAiDocumentAttachmentSchema>;

export const WorkflowCollectIntakeFormInputSchema = z.object({
  suggestedValues: IntakeFormValuesSchema.optional().describe(
    'Best-effort values inferred from the conversation, attached documents, and user context, keyed by canonical intake-form field name. Omit fields that cannot be inferred.'
  ),
  templateWorkflowId: WorkflowIdSchema.describe(
    'Verified template workflow ID whose canonical intake form the user should complete.'
  ),
});
export type WorkflowCollectIntakeFormInput = z.infer<typeof WorkflowCollectIntakeFormInputSchema>;

export const WorkflowCollectIntakeFormOutputSchema = z.union([
  z.object({ intakeFormValues: IntakeFormValuesSchema }),
  z.object({ skipped: z.literal(true) }),
]);
export type WorkflowCollectIntakeFormOutput = z.infer<typeof WorkflowCollectIntakeFormOutputSchema>;

export const WorkflowLaunchInstanceAssignmentSchema = z.object({
  email: z.email().optional(),
  name: z.string().min(1).optional(),
  permissionId: z.uuid(),
  permissionRole: z.enum(GQL.PermissionRole).optional(),
  userId: z.uuid().optional(),
});
export type WorkflowLaunchInstanceAssignment = z.infer<
  typeof WorkflowLaunchInstanceAssignmentSchema
>;

/**
 * Legacy client-input tool schema. New launches collect via the
 * `workflowLaunchTemplate` approval card; keep this for in-flight threads.
 */
export const WorkflowCollectLaunchInfoInputSchema = z.object({
  suggestedAlineFieldValues: z
    .record(z.string(), z.string())
    .optional()
    .describe('Best-effort launch document-field values keyed by field ID.'),
  suggestedIntakeValues: IntakeFormValuesSchema.optional().describe(
    'Best-effort intake values keyed by canonical intake-form field name.'
  ),
  suggestedRoleAssignments: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      'Optional role-id to assignee-email hints used to prefill the launch card. Only well-formed emails prefill a role; omit a role rather than guessing.'
    ),
  templateWorkflowId: WorkflowIdSchema.describe(
    'Verified template workflow ID whose launch requirements the user should complete.'
  ),
});
export type WorkflowCollectLaunchInfoInput = z.infer<typeof WorkflowCollectLaunchInfoInputSchema>;

export const WorkflowCollectLaunchInfoSubmittedSchema = z.object({
  alineFieldValues: z.record(z.string(), z.string()).optional(),
  instanceAssignments: z.array(WorkflowLaunchInstanceAssignmentSchema).optional(),
  intakeFormValues: IntakeFormValuesSchema.optional(),
  status: z.literal('submitted'),
});
export type WorkflowCollectLaunchInfoSubmitted = z.infer<
  typeof WorkflowCollectLaunchInfoSubmittedSchema
>;

/**
 * Discriminated on `status` so a skip cannot collapse into an empty submitted
 * payload. Every field on the submitted branch is optional, so without a
 * required discriminant `{ skipped: true }` would match submitted and strip
 * the skip key — satisfying launch preconditions after the user declined.
 *
 * Kept for legacy in-flight `workflowCollectLaunchInfo` tool parts. New launches
 * collect these values on the `workflowLaunchTemplate` approval card instead.
 */
export const WorkflowCollectLaunchInfoOutputSchema = z.discriminatedUnion('status', [
  WorkflowCollectLaunchInfoSubmittedSchema,
  z.object({ status: z.literal('skipped') }),
]);
export type WorkflowCollectLaunchInfoOutput = z.infer<typeof WorkflowCollectLaunchInfoOutputSchema>;

/**
 * Launch-template tool input. The model supplies template id, name, attachments,
 * and optional suggestions. The approval card may patch collected launch values
 * (`alineFieldValues`, `instanceAssignments`, `intakeFormValues`) on approve.
 */
export const WorkflowLaunchTemplateInputSchema = z.object({
  alineFieldValues: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      'Collected launch document-field values keyed by field ID. Filled by the approval card on approve — do not invent these.'
    ),
  documentAttachments: z
    .array(WorkflowAiDocumentAttachmentSchema)
    .max(50)
    .optional()
    .describe(
      'Optional extra documents to attach to the launched workflow with explicit workflow roles.'
    ),
  instanceAssignments: z
    .array(WorkflowLaunchInstanceAssignmentSchema)
    .optional()
    .describe(
      'Collected role assignments for the launched workflow. Filled by the approval card on approve — do not invent these.'
    ),
  intakeFormValues: IntakeFormValuesSchema.optional().describe(
    'Collected intake values keyed by field name. Filled by the approval card on approve — do not invent or reconstruct these.'
  ),
  name: z
    .string()
    .min(1)
    .max(WORKFLOW_AI_NAME_MAX_LENGTH)
    .describe(
      'User-facing title for the launched workflow. Make it specific to the request, usually combining the workflow type and counterparty/project, e.g. "NDA for Acme", "Vendor MSA with Globex", or "DPA for Salesforce". Do not use raw ids. If the user gave an explicit title, use it. If there is no counterparty/project context, use the selected template title.'
    ),
  suggestedAlineFieldValues: z
    .record(z.string(), z.string())
    .optional()
    .describe('Best-effort launch document-field values keyed by field ID.'),
  suggestedIntakeValues: IntakeFormValuesSchema.optional().describe(
    'Best-effort intake values keyed by canonical intake-form field name.'
  ),
  suggestedRoleAssignments: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      'Optional role-id to assignee-email hints used to prefill the launch card. Only well-formed emails prefill a role; omit a role rather than guessing.'
    ),
  templateWorkflowId: WorkflowIdSchema.describe(
    'Template workflow ID returned by workflowSearchTemplates or explicitly supplied by the user. Never invent this value.'
  ),
});
export type WorkflowLaunchTemplateInput = z.infer<typeof WorkflowLaunchTemplateInputSchema>;

export const WorkflowAiActionSchema = z.enum([
  'search_existing',
  'search_templates',
  'launch_template',
  'create_draft',
]);
export type WorkflowAiAction = z.infer<typeof WorkflowAiActionSchema>;

export const WorkflowAiCandidateKindSchema = z.enum(['workflow', 'template']);
export type WorkflowAiCandidateKind = z.infer<typeof WorkflowAiCandidateKindSchema>;

export const WorkflowAiCandidateSchema = z.object({
  confidenceReason: z.string(),
  description: z.string(),
  kind: WorkflowAiCandidateKindSchema,
  score: z.number().nonnegative(),
  state: z.nativeEnum(GQL.WorkflowState),
  status: z.nativeEnum(GQL.WorkflowStatus),
  title: z.string().max(WORKFLOW_AI_NAME_MAX_LENGTH),
  url: InternalPathSchema,
  workflowId: WorkflowIdSchema,
});
export type WorkflowAiCandidate = z.infer<typeof WorkflowAiCandidateSchema>;

export const WorkflowAiDraftTaskSchema = z.object({
  assignToAi: z
    .boolean()
    .optional()
    .describe(
      'Delegate this task to Aline AI to do the work. Aline AI runs it automatically once its phase is active (never for template workflows). Defaults to false (unassigned).'
    ),
  description: z
    .string()
    .max(DESCRIPTION_MAX_LENGTH)
    .optional()
    .describe('Optional longer description or instructions for the task.'),
  title: z
    .string()
    .min(1)
    .max(WORKFLOW_AI_NAME_MAX_LENGTH)
    .describe('Short, action-oriented title of the task.'),
});
export type WorkflowAiDraftTask = z.infer<typeof WorkflowAiDraftTaskSchema>;

export const WorkflowAiDraftPhaseSchema = z.object({
  description: z
    .string()
    .max(DESCRIPTION_MAX_LENGTH)
    .optional()
    .describe('Optional description of what happens in this phase.'),
  isSigning: z
    .boolean()
    .optional()
    .describe(
      'Mark this phase as the signing phase. At most ONE phase per workflow may set this to true.'
    ),
  name: z
    .string()
    .min(1)
    .max(WORKFLOW_AI_NAME_MAX_LENGTH)
    .describe('Short, human-readable phase name (e.g. "Initial Review").'),
  tasks: z
    .array(WorkflowAiDraftTaskSchema)
    .optional()
    .describe('Tasks the user (or assignees) should complete during this phase.'),
});
export type WorkflowAiDraftPhase = z.infer<typeof WorkflowAiDraftPhaseSchema>;

export const WorkflowAiCreatedWorkflowSchema = z.object({
  phaseCount: z.number().int().nonnegative().optional(),
  taskCount: z.number().int().nonnegative().optional(),
  workflowId: WorkflowIdSchema,
});
export type WorkflowAiCreatedWorkflow = z.infer<typeof WorkflowAiCreatedWorkflowSchema>;

const WorkflowAiBaseResultSchema = z.object({
  reasoningSummary: z.string(),
  type: z.literal('workflow-result'),
});

const WorkflowAiTemplateCandidatesResultSchema = WorkflowAiBaseResultSchema.extend({
  candidates: z.array(WorkflowAiCandidateSchema),
  clarificationQuestion: z.string().nullable(),
  status: z.literal('template_candidates'),
});

const WorkflowAiTemplateCandidatesToolCallResultSchema = WorkflowAiBaseResultSchema.extend({
  candidates: z.array(WorkflowAiCandidateSchema),
  status: z.literal('template_candidates'),
});

const WorkflowAiDuplicatesFoundResultSchema = WorkflowAiBaseResultSchema.extend({
  candidates: z.array(WorkflowAiCandidateSchema),
  status: z.literal('duplicates_found'),
});

const WorkflowAiLaunchedResultSchema = WorkflowAiBaseResultSchema.extend({
  status: z.literal('launched'),
  // Absent on results persisted before launches recorded their template.
  templateName: z.string().optional(),
  workflow: WorkflowAiCreatedWorkflowSchema,
});

export const WorkflowAiCreatedDraftResultSchema = WorkflowAiBaseResultSchema.extend({
  status: z.literal('created_draft'),
  workflow: WorkflowAiCreatedWorkflowSchema,
});
export type WorkflowAiCreatedDraftResult = z.infer<typeof WorkflowAiCreatedDraftResultSchema>;

const WorkflowAiErroredResultSchema = WorkflowAiBaseResultSchema.extend({
  errorMessage: z.string().max(DESCRIPTION_MAX_LENGTH),
  status: z.literal('errored'),
});

const WorkflowAiNoMatchResultSchema = WorkflowAiBaseResultSchema.extend({
  candidates: z.array(WorkflowAiCandidateSchema),
  status: z.literal('no_match'),
});

export const WorkflowAiCreateDraftToolCallResultSchema = z.discriminatedUnion('status', [
  WorkflowAiCreatedDraftResultSchema,
  WorkflowAiErroredResultSchema,
]);

export const WorkflowAiResultSchema = z.discriminatedUnion('status', [
  WorkflowAiDuplicatesFoundResultSchema,
  WorkflowAiTemplateCandidatesResultSchema,
  WorkflowAiLaunchedResultSchema,
  WorkflowAiCreatedDraftResultSchema,
  WorkflowAiNoMatchResultSchema,
  WorkflowAiErroredResultSchema,
]);
export type WorkflowAiResult = z.infer<typeof WorkflowAiResultSchema>;

export const WorkflowAiToolCallResultSchema = z.union([
  WorkflowAiResultSchema,
  WorkflowAiTemplateCandidatesToolCallResultSchema,
]);
export type WorkflowAiToolCallResult = z.infer<typeof WorkflowAiToolCallResultSchema>;
